// OptionsTimeStepsForm.jsx
import { useState } from "react"
import { Grid } from '@mui/material'
import "../../css/DemoCode.css"

/**
 * Options Time Steps Form is a group of data inputs usually shown when clicking on Options->Time Steps. 
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @param {JSON} fullModel The 'full' SWMM model that contains supporting data for this component.
 * @returns UI for interactive swmm modeling.
 */
export default function OptionsTimeStepsForm({model, setModel, fullModel}) {
    const [controlRuleStep,   setControlRuleStep]   = useState(fullModel.OPTIONS.RULE_STEP        ?fullModel.OPTIONS.RULE_STEP        :'00:00:00')
    
    const [reportingStep, setReportingStep] = useState(fullModel.OPTIONS.REPORT_STEP?fullModel.OPTIONS.REPORT_STEP:'00:00:00')
    const [runoffDryStep, setRunoffDryStep] = useState(fullModel.OPTIONS.DRY_STEP?fullModel.OPTIONS.DRY_STEP      :'00:00:00')
    const [runoffWetStep, setRunoffWetStep] = useState(fullModel.OPTIONS.WET_STEP?fullModel.OPTIONS.WET_STEP      :'00:00:00')
    const [routingStep,   setRoutingStep  ] = useState(fullModel.OPTIONS.ROUTING_STEP?fullModel.OPTIONS.ROUTING_STEP:'0')

    const [skipSteadyFlowPeriods, setSkipSteadyFlowPeriods] = useState(fullModel.OPTIONS.SKIP_STEADY_STATE?fullModel.OPTIONS.SKIP_STEADY_STATE==='YES'?true:false:false)
    
    const [systemFlowTolerance,   setSystemFlowTolerance  ] = useState(fullModel.OPTIONS.SYS_FLOW_TOL?fullModel.OPTIONS.SYS_FLOW_TOL:'0')
    const [lateralFlowTolerance,  setLateralFlowTolerance ] = useState(fullModel.OPTIONS.LAT_FLOW_TOL?fullModel.OPTIONS.LAT_FLOW_TOL:'0')

  const updateControlRuleStep = (val) => {
    setControlRuleStep(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          RULE_STEP: val
        }
      }))
    }
  }
  
  const updateReportingStep = (val) => {
    setReportingStep(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          REPORT_STEP: val
        }
      }))
    }
  }
  
  const updateRunoffDryStep = (val) => {
    setRunoffDryStep(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          DRY_STEP: val
        }
      }))
    }
  }
  
  const updateRunoffWetStep = (val) => {
    setRunoffWetStep(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          WET_STEP: val
        }
      }))
    }
  }
  
  const updateRoutingStep = (val) => {
    setRoutingStep(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          ROUTING_STEP: val
        }
      }))
    }
  }

  const updateSkipSteadyFlowPeriods = (val) => {
    setSkipSteadyFlowPeriods(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          SKIP_STEADY_STATE: val===true?'YES':'NO'
        }
      }))
    }
  }
  
  const updateSystemFlowTolerance = (val) => {
    setSystemFlowTolerance(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          SYS_FLOW_TOL: val
        }
      }))
    }
  }

  const updateLateralFlowTolerance = (val) => {
    setLateralFlowTolerance(val)
    
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          LAT_FLOW_TOL: val
        }
      }))
    }
  }

  return(
    <>
    {
      <>
      <Grid container>
        <Grid item xs={12}>
        {(
          <>
          <label className='editor-small' >Reporting Step
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateReportingStep(e.target.value)}}
                    value={reportingStep}
                    id={'OptionsReportingStep'}
            />
          </label>
          <label className='editor-small' >Runoff Step: Dry Weather
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateRunoffDryStep(e.target.value)}}
                    value={runoffDryStep}
                    id={'OptionsRunoffDryStep'}
            />
          </label>
          <label className='editor-small' >Runoff Step: Wet Weather
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateRunoffWetStep(e.target.value)}}
                    value={runoffWetStep}
                    id={'OptionsRunoffWetStep'}
            />
          </label>
          <label className='editor-small' >Control Rule Step
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateControlRuleStep(e.target.value)}}
                    value={controlRuleStep}
                    id={'OptionsControlRuleStep'}
            />
          </label>
          <label className='editor-small' >Routing Step
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateRoutingStep(e.target.value)}}
                    value={routingStep}
                    id={'OptionsRoutingStep'}
            />
          </label>
        {(
          <>
          <div style={{borderBottom:'1px solid #fff', marginTop: '15px'}}>Process Models</div>
          <label className='editor-small' >
            <input  type = "checkbox"
                    style={{display: 'inline'}}
                    onChange={(e)=>{updateSkipSteadyFlowPeriods(e.target.checked)}}
                    checked={skipSteadyFlowPeriods}
                    id={'OptionsSkipSteadyFlowPeriods'}
            />Skip Steady Flow Periods
          </label>
          <label className='editor-small' >System Flow Tolerance %
            <input  type = "number"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateSystemFlowTolerance(e.target.value)}}
                    value={systemFlowTolerance}
                    id={'OptionsSystemFlowTolerance'}
            />
          </label>
          <label className='editor-small' >Lateral Flow Tolerance %
            <input  type = "number"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateLateralFlowTolerance(e.target.value)}}
                    value={lateralFlowTolerance}
                    id={'OptionsLateralFlowTolerance'}
            />
          </label>
          </>
        )}
          </>
        )}
        </Grid>
      </Grid>
      </>
    }
    </>
  )
} 